/**
 * SuperAdminApprovals — TRUS-513
 *
 * The queue where a super_admin grant waits for a SECOND super admin.
 *
 * Inviting someone as Platform Admin in CBO 1 does not send anything: the
 * backend parks the invitation at `pending_approval` and writes a row to
 * `super_admin_grant_requests`. Until somebody approves it here, nothing is
 * emailed and no access exists. The requester cannot approve their own request —
 * the API refuses it, and the buttons below are disabled with the reason shown,
 * so the rule is visible rather than discovered as an error.
 *
 * Rendered inside User Management → Platform Admins (super_admin only).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Shield, Check, X, Loader2, RotateCcw } from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { cn } from '@/lib/utils';

interface SuperAdminRequest {
  id: number;
  request_type: string;
  cbo_id: number;
  target_email: string;
  target_name?: string | null;
  invitation_id?: number | null;
  requested_role: string;
  status: string;
  requested_by: number;
  requested_by_name?: string | null;
  requested_by_email?: string | null;
  requested_at?: string | null;
  decided_by_name?: string | null;
  decided_at?: string | null;
  decision_notes?: string | null;
  expires_at?: string | null;
  organization_name?: string | null;
  self_requested?: boolean;
}

type Filter = 'pending' | 'all';

const authHeaders = (cboId?: number) => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
  ...(cboId ? { 'X-CBO-ID': String(cboId) } : {}),
});

const fmtDate = (value?: string | null) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
};

export const SuperAdminApprovals: React.FC<{ cboId?: number; onChanged?: () => void }> = ({
  cboId,
  onChanged,
}) => {
  const [requests, setRequests] = useState<SuperAdminRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<Filter>('pending');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${getWeaveinApiUrl()}/api/super-admin-requests?status=${filter}`,
        { headers: authHeaders(cboId) },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setRequests(await res.json());
    } catch (err: any) {
      // A failure here must not read as "no pending requests" — that would hide
      // a queued grant from the only people who can act on it.
      setError('Could not load super admin requests. Refresh to try again.');
      setRequests([]);
      console.error('TRUS-513: failed to load super admin requests', err);
    } finally {
      setLoading(false);
    }
  }, [filter, cboId]);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (id: number, action: 'approve' | 'reject') => {
    const verb = action === 'approve' ? 'Approve' : 'Reject';
    const target = requests.find((r) => r.id === id);
    if (
      !window.confirm(
        `${verb} platform admin access for ${target?.target_email}?` +
          (action === 'approve'
            ? '\n\nThis sends the invitation and grants full access to every organization once they register.'
            : '\n\nThe invitation will be cancelled.'),
      )
    ) {
      return;
    }
    setBusyId(id);
    setError(null);
    setNotice(null);
    try {
      const res = await fetch(`${getWeaveinApiUrl()}/api/super-admin-requests/${id}/${action}`, {
        method: 'POST',
        headers: authHeaders(cboId),
        body: JSON.stringify({}),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || `HTTP ${res.status}`);
      setNotice(
        action === 'approve'
          ? `Approved — invitation sent to ${body?.target_email || target?.target_email}.`
          : `Rejected the request for ${body?.target_email || target?.target_email}.`,
      );
      await load();
      onChanged?.();
    } catch (err: any) {
      setError(err?.message || `Failed to ${action} the request`);
    } finally {
      setBusyId(null);
    }
  };

  const pendingCount = requests.filter((r) => r.status === 'pending').length;

  return (
    <div className="mb-4 border border-amber-200 dark:border-amber-800 rounded-lg bg-amber-50/60 dark:bg-amber-900/20">
      <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-amber-200 dark:border-amber-800">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-amber-900 dark:text-amber-200">
          <Shield className="w-4 h-4" />
          Super Admin Approvals
          {filter === 'pending' && pendingCount > 0 && (
            <span className="px-2 py-0.5 rounded-full text-xs bg-amber-200 text-amber-900 dark:bg-amber-800 dark:text-amber-100">
              {pendingCount}
            </span>
          )}
        </h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setFilter(filter === 'pending' ? 'all' : 'pending')}
            className="text-xs px-2 py-1 rounded border border-amber-300 dark:border-amber-700 text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40"
          >
            {filter === 'pending' ? 'Show decided' : 'Show pending only'}
          </button>
          <button
            onClick={load}
            aria-label="Refresh"
            className="p-1 rounded text-amber-800 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900/40"
          >
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <p className="px-4 pt-3 text-xs text-amber-800 dark:text-amber-300">
        Platform Admin (super admin) access is granted only in the platform organization, and only
        after a <strong>second</strong> super admin approves. You cannot approve a request you
        raised yourself.
      </p>

      {error && (
        <div className="mx-4 mt-3 px-3 py-2 rounded text-xs bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200">
          {error}
        </div>
      )}
      {notice && (
        <div className="mx-4 mt-3 px-3 py-2 rounded text-xs bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200">
          {notice}
        </div>
      )}

      <div className="p-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-amber-800 dark:text-amber-300">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading requests…
          </div>
        ) : requests.length === 0 ? (
          <div className="text-sm text-amber-800/80 dark:text-amber-300/80">
            {filter === 'pending' ? 'No requests awaiting approval.' : 'No super admin requests yet.'}
          </div>
        ) : (
          <div className="overflow-x-auto bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  {['Requested for', 'Organization', 'Requested by', 'Raised', 'Status', 'Actions'].map((h) => (
                    <th
                      key={h}
                      className="px-4 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {requests.map((r) => {
                  const isPending = r.status === 'pending';
                  const blocked = !!r.self_requested;
                  return (
                    <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 dark:text-white">
                          {r.target_name || r.target_email}
                        </div>
                        {r.target_name && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">{r.target_email}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {r.organization_name || `CBO ${r.cbo_id}`}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {r.requested_by_name || r.requested_by_email || `User ${r.requested_by}`}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {fmtDate(r.requested_at)}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            'text-xs px-2 py-0.5 rounded-full capitalize',
                            isPending
                              ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                              : r.status === 'approved'
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
                          )}
                        >
                          {r.status}
                        </span>
                        {!isPending && r.decided_by_name && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            by {r.decided_by_name} · {fmtDate(r.decided_at)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {!isPending ? (
                          <span className="text-xs text-gray-400">—</span>
                        ) : blocked ? (
                          // Spell out why, rather than letting the click 403.
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            You raised this — another super admin must decide.
                          </span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <button
                              disabled={busyId === r.id}
                              onClick={() => decide(r.id, 'approve')}
                              className="inline-flex items-center gap-1 px-2 py-1 text-sm rounded text-green-700 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 disabled:opacity-50"
                            >
                              {busyId === r.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Check className="w-4 h-4" />
                              )}
                              <span>Approve</span>
                            </button>
                            <button
                              disabled={busyId === r.id}
                              onClick={() => decide(r.id, 'reject')}
                              className="inline-flex items-center gap-1 px-2 py-1 text-sm rounded text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
                            >
                              <X className="w-4 h-4" />
                              <span>Reject</span>
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SuperAdminApprovals;
